import React, { Component } from 'react';
import { Button, Collapse, Flex, Heading, Icon, Spinner, Text } from 'core/components';
import { inject, observer } from 'mobx-react';
import { TbLayoutSidebar } from 'react-icons/tb';
import { TOOL_CALL_STATUS, isMcpTool } from '@kentik/ui-shared/kaizen/constants';
import { ToolCallCard } from './KaizenChatSession.styles';
import { createToolResultComponent, hasToolResultView, TOOL_NAMES } from './ToolResultFactory';
import { KaizenToolResultConfirmActions } from './KaizenToolResultConfirmActions';
import { getAgentDisplayName } from './util/agentDisplayNames';

const SEND_TO_EXPLORER_TOOLS = [TOOL_NAMES.DATA_EXPLORER, TOOL_NAMES.SNMP_TRAP, TOOL_NAMES.NMS];

// Tools that delegate work to a dedicated sub-agent. While these are pending, the main
// agent is waiting for the sub-agent to run its own tools and respond.
const SUBAGENT_TOOL_DISPLAY_NAMES = {
  [TOOL_NAMES.MATH_AGENT]: 'Math Agent',
  [TOOL_NAMES.SYNTHETICS_MONITORING_AGENT]: 'Synthetics Agent'
};

@inject('$auth')
@observer
class KaizenToolResult extends Component {
  _flashTimeout = null;

  state = {
    shouldFlash: false,
    isExpanded: false,
    isApprovingTool: false
  };

  componentDidUpdate(prevProps, prevState) {
    const { tool, sessionModel } = this.props;
    const prevStatus = prevProps.tool?.status;
    const currStatus = tool?.status;
    const prevAwaitingApproval = prevProps.tool?.awaitingApproval;
    const currAwaitingApproval = tool?.awaitingApproval;

    // Flash logic
    if (
      (!prevStatus || prevStatus === 'PROCESSING') &&
      currStatus === TOOL_CALL_STATUS.SUCCESS &&
      !prevState.shouldFlash
    ) {
      clearTimeout(this._flashTimeout);
      setTimeout(() => {
        this.setState({ shouldFlash: true });
        this._flashTimeout = setTimeout(() => {
          this.setState({ shouldFlash: false });
        }, 900);
      }, 0);
    }

    // Auto-approval check when status changes to awaiting approval
    const isNowAwaitingApproval = currStatus === TOOL_CALL_STATUS.AWAITING_APPROVAL || currAwaitingApproval;
    const wasAwaitingApproval = prevStatus === TOOL_CALL_STATUS.AWAITING_APPROVAL || prevAwaitingApproval;

    if (isNowAwaitingApproval && !wasAwaitingApproval) {
      if (tool && sessionModel && this.isAutoApproved(sessionModel.id, tool.tool_name)) {
        // Auto-approve after a small delay to ensure the UI has updated
        setTimeout(() => {
          this.handleApprove(false);
        }, 100);
      }
    }
  }

  componentDidMount() {
    // Check if this tool is already auto-approved
    const { tool, sessionModel } = this.props;
    if (tool && sessionModel && this.isAutoApproved(sessionModel.id, tool.tool_name)) {
      const isAwaitingApproval = tool.status === TOOL_CALL_STATUS.AWAITING_APPROVAL || tool.awaitingApproval;
      if (isAwaitingApproval) {
        this.handleApprove(false);
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this._flashTimeout);
  }

  // Cookie helper functions
  setAutoApprovalCookie(sessionId, toolName) {
    const cookieName = `kaizen_auto_approve_${sessionId}`;
    const existingValue = this.getCookie(cookieName);
    const toolNames = existingValue ? existingValue.split(',') : [];

    if (!toolNames.includes(toolName)) {
      toolNames.push(toolName);
    }

    // Set cookie to expire at end of session
    document.cookie = `${cookieName}=${toolNames.join(',')};path=/;SameSite=Lax`;
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return null;
  }

  isAutoApproved(sessionId, toolName) {
    const cookieName = `kaizen_auto_approve_${sessionId}`;
    const value = this.getCookie(cookieName);
    if (value) {
      const toolNames = value.split(',');
      return toolNames.includes(toolName);
    }
    return false;
  }

  handleToggleExpanded = () => {
    this.setState((prevState) => ({
      isExpanded: !prevState.isExpanded
    }));
  };

  handleSendToExplorer = () => {
    const { tool, onSendToExplorer } = this.props;
    if (onSendToExplorer && tool.query_object) {
      onSendToExplorer(tool.query_object, tool.raw_result);
    }
  };

  handleClick = () => {
    const { tool, onSelectToolResult, isCopilotMode, onSendToExplorer } = this.props;
    const hasDetailView = hasToolResultView(tool.tool_name);
    const canSendToExplorer = onSendToExplorer && SEND_TO_EXPLORER_TOOLS.includes(tool.tool_name);

    if (hasDetailView) {
      if (isCopilotMode && !canSendToExplorer) {
        // In copilot mode for non-DE tools, toggle inline expansion
        this.handleToggleExpanded();
      } else if (!isCopilotMode) {
        // In normal mode, open in side panel
        onSelectToolResult(tool);
      }
      // When canSendToExplorer is true, card click does nothing — use the button
    }
  };

  handleApprove = (alwaysApprove = false) => {
    const { tool, sessionModel, $auth } = this.props;
    this.setState({ isApprovingTool: true });

    if (alwaysApprove) {
      this.setAutoApprovalCookie(sessionModel.id, tool.tool_name);
    }

    // Get the approving user's display name using the auth store's activeUserDisplay
    // This properly handles sudo/spoofing scenarios
    const approverName = $auth.activeUserDisplay || 'Unknown User';

    // Store approver name on the tool (this updates the UI immediately)
    tool.approved_by_user_name = approverName;

    sessionModel
      .approveToolCall(tool.tool_tracking_id, true)
      .then(() => {
        this.setState({ isApprovingTool: false });
      })
      .catch((err) => {
        console.error('Failed to approve tool:', err);
        this.setState({ isApprovingTool: false });
      });
  };

  handleReject = () => {
    const { tool, sessionModel } = this.props;
    this.setState({ isApprovingTool: true });
    sessionModel
      .approveToolCall(tool.tool_tracking_id, false)
      .then(() => {
        this.setState({ isApprovingTool: false });
      })
      .catch((err) => {
        console.error('Failed to reject tool:', err);
        this.setState({ isApprovingTool: false });
      });
  };

  getDetailViewButtonText = (isCopilotMode, isExpanded) => {
    if (isCopilotMode) {
      return isExpanded ? 'Collapse' : 'Expand';
    }
    return 'View details';
  };

  getDetailViewButtonIcon = (isCopilotMode, isExpanded) => {
    if (isCopilotMode) {
      return isExpanded ? 'caret-up' : 'caret-down';
    }
    return TbLayoutSidebar;
  };

  renderDetailViewButton = (canSendToExplorer, isCopilotMode, isExpanded) => {
    if (canSendToExplorer) {
      return (
        <Button
          small
          intent="primary"
          text="Send to Data Explorer"
          icon="arrow-right"
          onClick={(e) => {
            e.stopPropagation();
            this.handleSendToExplorer();
          }}
        />
      );
    }

    return (
      <>
        <Text muted small>
          {this.getDetailViewButtonText(isCopilotMode, isExpanded)}
        </Text>
        <Icon icon={this.getDetailViewButtonIcon(isCopilotMode, isExpanded)} size={14} color="muted" />
      </>
    );
  };

  render() {
    const { tool, isCopilotMode, onDataViewCreate, onQueryError, sessionModel, onSendToExplorer, compact } = this.props;
    const { shouldFlash, isExpanded, isApprovingTool } = this.state;

    if (!tool) {
      return null;
    }

    const { title, status, awaitingApproval } = tool;
    const hasDetailView = hasToolResultView(tool.tool_name);
    const isSuccess = status === TOOL_CALL_STATUS.SUCCESS;
    const canSendToExplorer = onSendToExplorer && SEND_TO_EXPLORER_TOOLS.includes(tool.tool_name) && isSuccess;
    const isInteractive = (hasDetailView && isSuccess) || canSendToExplorer;
    const isFailed = status === TOOL_CALL_STATUS.FAILED;
    const isRejected = status === TOOL_CALL_STATUS.USER_REJECTED;
    const isTimeout = status === TOOL_CALL_STATUS.APPROVAL_TIMEOUT;
    const isAwaitingApproval = status === TOOL_CALL_STATUS.AWAITING_APPROVAL || awaitingApproval;
    const isAutoApproved = sessionModel && this.isAutoApproved(sessionModel.id, tool.tool_name);
    const shouldShowApprovalUI = isAwaitingApproval && !isAutoApproved;

    // When a sub-agent delegation tool is still running, surface that the main agent is
    // waiting on the sub-agent rather than leaving a bare spinner + title.
    const subAgentDisplayName = SUBAGENT_TOOL_DISPLAY_NAMES[tool.tool_name];
    const isPending = !status || status === TOOL_CALL_STATUS.PENDING || status === 'PROCESSING';
    const isWaitingForSubAgent = Boolean(subAgentDisplayName) && isPending && !shouldShowApprovalUI;

    const headerProps = {
      color: !status ? 'muted' : 'body',
      fontWeight: isInteractive ? 500 : 300
    };

    let cardClass = status ? status.toLowerCase() : '';
    if (status === TOOL_CALL_STATUS.SUCCESS && shouldFlash) {
      cardClass += ' flash';
    }

    if (isExpanded) {
      cardClass += ' expanded';
    }

    const toolResultCard = (
      <ToolCallCard
        interactive={isInteractive}
        elevation={(() => {
          if (compact) {
            return 0;
          }
          if (isInteractive) {
            return 2;
          }
          return 0;
        })()}
        className={cardClass}
        onClick={isInteractive ? this.handleClick : null}
        compact={compact}
      >
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" gap={1} flex={1}>
            {!status && <Spinner size={compact ? 10 : 12} />}
            {isFailed && <Icon icon="error" color="danger" size={compact ? 12 : 16} />}
            {isRejected && <Icon icon="error" size={compact ? 12 : 16} />}
            {isTimeout && <Icon icon="time" color="muted" size={compact ? 12 : 16} />}
            {shouldShowApprovalUI && <Icon icon="pause" color="muted" size={compact ? 12 : 16} />}
            {(status === TOOL_CALL_STATUS.SUCCESS || (isAwaitingApproval && isAutoApproved)) && (
              <Icon icon="tick-circle" color="success" className="success-icon" size={compact ? 12 : 16} />
            )}
            {compact ? (
              <Text fontSize={14} {...headerProps}>
                {title}
              </Text>
            ) : (
              <Heading level={6} m={0} {...headerProps}>
                {title}
              </Heading>
            )}
            {isMcpTool(tool.tool_name) && (
              <Flex alignItems="center" gap="4px" ml={1}>
                <Icon icon="data-connection" size={14} color="muted" />
                <Text small muted>
                  {getAgentDisplayName(tool.tool_name)}
                </Text>
              </Flex>
            )}
          </Flex>

          {compact && tool.latency_ms && status === TOOL_CALL_STATUS.SUCCESS && (
            <Text fontSize={11} color="muted" className="compact-hover-detail">
              {tool.latency_ms > 1000 ? `${(tool.latency_ms / 1000).toFixed(1)}s` : `${tool.latency_ms}ms`}
            </Text>
          )}

          {isFailed && (
            <Text small color="danger">
              <Text fontWeight="bold">Error:</Text> Something went wrong
            </Text>
          )}

          {isRejected && <Text fontWeight="bold">Rejected: Tool execution was not approved</Text>}

          {isTimeout && <Text fontWeight="bold">Timeout: Approval request timed out</Text>}

          {status === TOOL_CALL_STATUS.SUCCESS && hasDetailView && !compact && (
            <Flex alignItems="center" gap="6px">
              {this.renderDetailViewButton(canSendToExplorer, isCopilotMode, isExpanded)}
            </Flex>
          )}
        </Flex>

        {isWaitingForSubAgent && (
          <Flex alignItems="center" gap="6px" mt="2px" ml={compact ? '18px' : '24px'}>
            <Text small color="muted" fontStyle="italic">
              Waiting for {subAgentDisplayName} to respond…
            </Text>
          </Flex>
        )}

        {shouldShowApprovalUI && (
          <KaizenToolResultConfirmActions
            tool={tool}
            onApprove={() => this.handleApprove(false)}
            onApproveAndAutoApprove={() => this.handleApprove(true)}
            onReject={this.handleReject}
            disabled={isApprovingTool}
          />
        )}

        {tool.approved_by_user_name &&
          (status === TOOL_CALL_STATUS.SUCCESS || status === TOOL_CALL_STATUS.USER_APPROVED) && (
            <Flex mt={1} alignItems="center" gap={1}>
              <Icon icon="tick" size={12} color="success" />
              <Text small color="muted">
                Approved by <strong>{tool.approved_by_user_name}</strong>
              </Text>
            </Flex>
          )}
      </ToolCallCard>
    );

    // In copilot mode, show inline expansion (but NOT for Send-to-Explorer tools)
    if (isCopilotMode && hasDetailView && status === TOOL_CALL_STATUS.SUCCESS && !canSendToExplorer) {
      return (
        <Flex flexDirection="column" gap={0.5}>
          {toolResultCard}
          {isExpanded && (
            <Collapse isOpen={isExpanded}>
              {createToolResultComponent(tool, {
                onCloseToolResult: () => this.setState({ isExpanded: false }),
                onDataViewCreate,
                onQueryError,
                isInlineMode: true
              })}
            </Collapse>
          )}
        </Flex>
      );
    }

    // In normal mode, just show the card
    return toolResultCard;
  }
}

export default KaizenToolResult;
